import { EndUsersUploadedError } from '@/types/common'

import { Upload } from '@phosphor-icons/react'
import { parse } from 'csv-parse/browser/esm/sync'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import readXlsxFile from 'read-excel-file'
import CampaignButtons from '../../../components/campaign-buttons'
import { CsvFieldsMapping } from '../../../components/csv/csv-field-mapping'
import CSVFieldSelector from '../../../components/csv/csv-field-selector'
import CSVPreview from '../../../components/csv/csv-preview'
import FileUpload from '../../../components/file-upload'
import { activeStepState } from '../../../store/atoms/active-step'
import { tokenState } from '../../../store/atoms/token'
import { createPortal } from 'react-dom'
import ErrorModal from '@/components/upload-end-users-error-modal'
import { GetCampaignFragment } from '@/types/campaign'
import { Separator } from '@alphabait/ui'
import OneCommunityPurchases from './integrations/one-community-purchases'
import OneCommunityForm from './integrations/one-community-form'
import OneCommunityThreeDrawsAndPrize from './integrations/one-community-three-draws-and-prize'
import RadioGroup from '@/components/radio-group'
import { parseFlexibleDate } from '@/lib/utils'
import { format } from 'date-fns'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCampaignClient } from '@/lib/api/ts-rest-client'
import { CACHE_KEYS } from '@/lib/cache-utils'

// Type for EndUser from the contract
type EndUser = {
  id: string
  campaignId?: string | null
  email?: string | null
  firstName?: string | null
  middle?: string | null
  lastName?: string | null
  company?: string | null
  address?: any
  birthday?: string | null
  campaignIncentiveId?: string | null
  questionnaireAnswers?: any
  referralOf?: string | null
  happinessAnswers?: any
  phoneNumber?: string | null
  sku?: string | null
  digitalCode?: string | null
  postnlCode?: string | null
  referenceCode?: number | null
  uniqueCode?: string | null
  isTest?: boolean | null
}

const tableHeaders = [
  'end_users.table_header.first_name',
  'end_users.table_header.middle',
  'end_users.table_header.last_name',
  'end_users.table_header.company',
  'end_users.table_header.email',
  'end_users.table_header.phone_number',
  'end_users.table_header.postal_code',
  'end_users.table_header.city',
  'end_users.table_header.street_name',
  'end_users.table_header.house_number',
  'end_users.table_header.country',
  'end_users.table_header.birthday',
  'end_users.table_header.sku',
  'end_users.table_header.digital_code',
  'end_users.table_header.postnl_code',
  'end_users.table_header.reference_code',
]
type ParsedCsv = {
  [key: string]: string
}[]
const isParsedCsv = (data: ParsedCsv): data is ParsedCsv => {
  return (data as ParsedCsv)[0]['Voornaam'] ? true : false
}

export default function EndUsersSetup() {
  const { campaign } = useOutletContext<{ campaign: GetCampaignFragment }>()

  const setActiveStep = useSetRecoilState(activeStepState)
  const token = useRecoilValue(tokenState)
  const { t } = useTranslation()
  const currentCampaignId = useParams().id
  const campaignClient = useCampaignClient()
  const queryClient = useQueryClient()
  const [oneCommunityIntegrationType, setOneCommunityIntegrationType] =
    useState<string>(
      campaign?.optionalSettings?.['oneCommunity']?.['integrationType'] ||
      'purchases',
    )

  useEffect(() => {
    const hasReferralIncentives =
      campaign?.referralCampaign && !campaign?.reuseSameIncentives
    const stepNumber = campaign?.referralCampaign
      ? hasReferralIncentives
        ? 10
        : 9
      : 8

    setActiveStep(stepNumber)
  }, [setActiveStep, campaign?.referralCampaign, campaign?.reuseSameIncentives])

  useEffect(() => {
    setOneCommunityIntegrationType(
      campaign?.optionalSettings?.['oneCommunity']?.['integrationType'],
    )
  }, [campaign?.optionalSettings?.['oneCommunity']?.['integrationType']])

  const navigate = useNavigate()
  const [file, setFile] = useState<File | undefined>(undefined)
  const [parsedCsv, setParsedCsv] = useState<ParsedCsv>()
  const [hasCsvError, setHasCsvError] = useState<boolean>(false)
  const [showModal, setShowModal] = useState(false)
  const isOneCommunityIntegration = useMemo(
    () =>
      campaign?.customer?.integrations?.find(
        (integration: any) => integration?.type === 'ONE_COMMUNITY',
      ),
    [campaign?.customer?.integrations],
  )
  const [errors, setErrors] = useState<EndUsersUploadedError[]>()

  const END_USER_FIELDS_MAPPING: CsvFieldsMapping = {
    firstName: {
      displayName: 'end_users.table_header.first_name',
      mappedName: 'Voornaam',
    },
    middle: {
      displayName: 'end_users.table_header.middle',
      mappedName: 'Tussenvoegsel',
    },
    lastName: {
      displayName: 'end_users.table_header.last_name',
      mappedName: 'Achternaam',
    },
    company: {
      displayName: 'end_users.table_header.company',
      mappedName: 'Bedrijfsnaam',
    },
    email: {
      displayName: 'end_users.table_header.email',
      mappedName: 'E-mail',
    },
    phoneNumber: {
      displayName: 'end_users.table_header.phone_number',
      mappedName: 'Telefoonnummer',
    },
    postalCode: {
      displayName: 'end_users.table_header.postal_code',
      mappedName: 'Postcode',
    },
    city: {
      displayName: 'end_users.table_header.city',
      mappedName: 'Plaats',
    },
    streetName: {
      displayName: 'end_users.table_header.street_name',
      mappedName: 'Straatnaam',
    },
    houseNumber: {
      displayName: 'end_users.table_header.house_number',
      mappedName: 'Huisnummer',
    },
    country: {
      displayName: 'end_users.table_header.country',
      mappedName: 'Land',
    },
    birthday: {
      displayName: 'end_users.table_header.birthday',
      mappedName: 'Geboortedag',
    },
    sku: {
      displayName: 'end_users.table_header.sku',
      mappedName: 'SKU',
    },
    digitalCode: {
      displayName: 'end_users.table_header.digital_code',
      mappedName: 'Code',
    },
    postnlCode: {
      displayName: 'end_users.table_header.postnl_code',
      mappedName: 'Dutch Post Code',
    },
    referenceCode: {
      displayName: 'end_users.table_header.reference_code',
      mappedName: 'Reference Code',
    },
  }

  const [endUserFieldsMapping, setEndUserFieldsMapping] =
    useState<CsvFieldsMapping>(END_USER_FIELDS_MAPPING)

  const [mappedFields, setMappedFields] = useState<{
    [field: string]: boolean | undefined
  }>({})

  // Fetch end users using ts-rest
  const { data: endUsersResponse, refetch } = useQuery({
    queryKey: ['endUsers', currentCampaignId],
    queryFn: async () => {
      if (!currentCampaignId) return null
      return await campaignClient.endUsers.query({
        params: { campaignId: currentCampaignId },
      })
    },
    enabled: !!currentCampaignId,
  })

  const endUsers = useMemo(
    () =>
      endUsersResponse?.status === 200
        ? endUsersResponse.body.endUsers || []
        : [],
    [endUsersResponse],
  )
  const totalEndUsers =
    endUsersResponse?.status === 200 ? endUsersResponse.body.total : 0

  const savedUsers = useMemo(() => {
    if (endUsers && endUsers.length > 0) {
      return endUsers.map((endUser: EndUser) => [
        endUser.firstName,
        endUser.middle,
        endUser.lastName,
        endUser.company,
        endUser.email,
        endUser.phoneNumber,
        endUser.address?.postcode,
        endUser.address?.city,
        endUser.address?.streetName,
        endUser.address?.houseNumber,
        endUser.address?.country,
        endUser.birthday,
        endUser.sku,
        endUser.digitalCode,
        endUser.postnlCode,
        endUser.referenceCode,
      ])
    }

    return []
  }, [endUsers])
  console.log({ savedUsers })
  const handleFileUpload = useCallback((file?: File) => {
    if (!file) {
      setParsedCsv(undefined)
      setFile(undefined)
      return
    }
    setFile(file)
    if (file.name.endsWith('.xlsx')) {
      readXlsxFile(file).then((rows) => {
        rows.shift()
        const header = rows.shift()
        if (!header) return
        const data = rows.map((row) => {
          return header.reduce(
            (all: Record<string, unknown>, current: unknown, i: number) => {
              all[current?.toString() || i] = row[i]
              return all
            },
            {},
          )
        })
        setParsedCsv(data as ParsedCsv)
        isParsedCsv(data as ParsedCsv)
          ? setHasCsvError(false)
          : setHasCsvError(true)
      })
    } else {
      const fr = new FileReader()
      fr.onload = () => {
        if (!fr.result) return
        const fileAsString = fr.result as string
        try {
          const parsedCsv = parse(fileAsString, {
            columns: true,
            skip_empty_lines: true,
          })
          setParsedCsv(parsedCsv)
          setHasCsvError(false)
        } catch (error) {
          error && setHasCsvError(true)
        }
      }

      fr.readAsText(file)
    }
  }, [])

  const handleFieldsMappingChanged = useCallback(
    (newFieldMapping: CsvFieldsMapping) => {
      setEndUserFieldsMapping(newFieldMapping)
    },
    [],
  )

  useEffect(() => {
    if (!parsedCsv) {
      setMappedFields({})
      return
    }

    const newMappedFields = Object.entries(endUserFieldsMapping).reduce(
      (mappedFields, [field, { mappedName }]) => {
        return {
          ...mappedFields,
          [field]: parsedCsv.some((row) => row[mappedName] !== undefined),
        }
      },
      {},
    )
    setMappedFields(newMappedFields)
  }, [endUserFieldsMapping, parsedCsv])

  const csvPreviewTableData = useMemo(() => {
    const headers = Object.entries(endUserFieldsMapping).map(
      ([, { displayName }]) => displayName,
    )
    const rows =
      parsedCsv?.slice(0, 15).map((row) => {
        return Object.entries(endUserFieldsMapping).map(
          ([field, { mappedName }]) => {
            if (field === 'birthday' && parseFlexibleDate(row[mappedName])) {
              return format(parseFlexibleDate(row[mappedName])!, 'yyyy-MM-dd')
            }

            return row[mappedName]
          },
        )
      }) || []
    return {
      headers,
      rows,
    }
  }, [parsedCsv, endUserFieldsMapping])

  const uploadEndUsersCsv = useCallback(async () => {
    if (!file || !currentCampaignId || !endUserFieldsMapping) return
    const formData = new FormData()
    formData.append('file', file as Blob)
    formData.append('campaignId', currentCampaignId?.toString())
    Object.entries(endUserFieldsMapping).forEach(([field, fieldDefinition]) => {
      formData.append(`mappedFields[${field}]`, fieldDefinition.mappedName)
    })
    try {
      const response = await fetch('/upload/end-users', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token?.__raw}`,
        },
      })
      const data = await response.json()

      // Parse message once if it's a JSON string
      let parsedMessage = data?.message
      if (typeof data?.message === 'string') {
        try {
          parsedMessage = JSON.parse(data?.message)
        } catch (parseError) {
          console.error('Error parsing message:', parseError)
          // Keep the original message string if parsing fails
        }
      }

      // Handle validation errors (array format from backend)
      if (
        Array.isArray(parsedMessage) &&
        parsedMessage.length > 0 &&
        parsedMessage[0]?.missingFields
      ) {
        setErrors(parsedMessage)
        setShowModal(true)
        return false
      }

      return true
    } catch (error) {
      console.error('Error uploading users:', error)
      return false
    }
  }, [file, currentCampaignId, token, endUserFieldsMapping])

  const onSave = useCallback(
    async (draft: boolean) => {
      const uploaded = await uploadEndUsersCsv()
      if (uploaded === false) return

      if (!draft) {
        await refetch()
        // Remove cached campaign list instead of invalidating it.
        // The global QueryClient has refetchOnMount: false and staleTime: 30s,
        // so invalidateQueries only marks data as stale — but the list page
        // won't refetch stale data on mount. removeQueries deletes the cache
        // entirely, forcing a fresh fetch when the list page mounts.
        queryClient.removeQueries({ queryKey: [CACHE_KEYS.CAMPAIGNS] })
        navigate(`/campaigns/confirmation/${currentCampaignId}`)
      }
    },
    [uploadEndUsersCsv, navigate, currentCampaignId, refetch, queryClient],
  )

  return (
    <div className="flex-1">
      <div className="flex flex-row">
        <h2 className="grow text-lg font-bold text-black-700">
          {t('end_users.data_upload')}
        </h2>
      </div>

      {isOneCommunityIntegration &&
        campaign?.campaignCategory !== 'lottery' && (
          <RadioGroup
            onChange={setOneCommunityIntegrationType}
            defaultValue={oneCommunityIntegrationType || 'purchases'}
            options={[
              {
                id: 'purchases',
                value: 'purchases',
                title: t('api_integration.purchases'),
                description: t('api_integration.purchases_description'),
              },
              {
                id: 'threeDrawsAndPrize',
                value: 'threeDrawsAndPrize',
                title: t('api_integration.three_draws_and_prize'),
                description: t(
                  'api_integration.three_draws_and_prize_description',
                ),
              },
            ]}
          />
        )}
      <div className="flex justify-between pt-4">
        <div>File</div>
        <div className="flex flex-row cursor-pointer">
          <Upload className="self-center mr-2" color="var(--primary)" />
          <a
            className="text-primary"
            href="/download/example.csv"
            download={true}
          >
            {t('end_users.download_example')}
          </a>
        </div>
      </div>
      <span className="relative">
        {showModal &&
          createPortal(
            <ErrorModal
              array={errors ?? []}
              onClick={() => setShowModal(false)}
            />,
            document.body,
          )}
      </span>

      <FileUpload
        allowedFileTypes={['csv', 'xlsx']}
        onFileUpload={handleFileUpload}
        disabled={
          campaign?.campaignStatus === 'active' ||
          campaign?.campaignStatus === 'completed'
        }
      />
      {hasCsvError && file && (
        <p className="text-primary">{t('upload_csv_error')}</p>
      )}
      {isOneCommunityIntegration && (
        <>
          <div className="my-6 flex gap-4 align-middle items-center justify-center">
            Or <Separator />
          </div>
          {oneCommunityIntegrationType === 'purchases' && (
            <OneCommunityPurchases
              campaign={campaign}
              onComplete={() => refetch()}
              setErrors={(errors) => setErrors(errors)}
              setShowModal={setShowModal}
            />
          )}
          {oneCommunityIntegrationType === 'threeDrawsAndPrize' && (
            <OneCommunityThreeDrawsAndPrize
              campaign={campaign}
              onComplete={() => refetch()}
              setErrors={(errors) => setErrors(errors)}
              setShowModal={setShowModal}
            />
          )}
          <OneCommunityForm
            campaign={campaign}
            onComplete={() => refetch()}
            setErrors={(errors) => setErrors(errors)}
            setShowModal={setShowModal}
          />
        </>
      )}

      {(parsedCsv && csvPreviewTableData && (
        <div className="flex">
          <div className="flex-1 w-96">
            <h2 className="grow my-4 text-lg font-bold text-black-700">
              {t('end_users.data_check')}
            </h2>
            <CSVFieldSelector
              fieldsMapping={endUserFieldsMapping}
              mappedFields={mappedFields}
              onFieldsMappingChanged={handleFieldsMappingChanged}
            />

            <CSVPreview
              csvPreview={csvPreviewTableData}
              totalRows={parsedCsv.length}
            />
          </div>
        </div>
      )) ||
        (savedUsers.length > 0 && (
          <div className="flex">
            <div className="flex-1 w-96">
              <CSVPreview
                csvPreview={{
                  headers: tableHeaders,
                  rows: savedUsers,
                }}
                totalRows={totalEndUsers}
              />
            </div>
          </div>
        ))}

      <CampaignButtons
        onSaveDraft={() => onSave(true)}
        onSubmit={() => onSave(false)}
        disableSubmit={errors && errors.length > 0}
      />
    </div>
  )
}
